import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Users, Mail, Phone, MessageSquare, CalendarCheck, TrendingUp, Megaphone, HandshakeIcon, Building2, Presentation as PresentationChart, DollarSign, ChevronDown, ArrowRight, Radio, Tv, Newspaper, Globe2, Share2, Youtube, FileText, MapPin, UserCheck } from 'lucide-react';
import type { CampaignPhase, CampaignStatus } from '../../lib/types';

interface TopStrategiesProps {
  phase: CampaignPhase;
  status: CampaignStatus;
  office?: string;
  location?: string;
}

interface MediaStrategy {
  title: string;
  description: string;
  icon: any;
  priority: 'high' | 'medium' | 'low';
  timeline: string;
  metrics: string;
  budget: string;
  channels: string[];
  targetAudience: string;
  effectiveness: number;
  insights: {
    keyMetrics: { label: string; value: string }[];
    timeline: { phase: string; activities: string[] }[];
    resources: { type: string; details: string }[];
    risks: { type: string; mitigation: string }[];
  };
}

interface CampaignPhaseDetails {
  name: string;
  description: string;
  startPercentage: number;
  endPercentage: number;
  strategies: string[];
}

const CAMPAIGN_PHASES: CampaignPhaseDetails[] = [
  {
    name: "Launch Phase",
    description: "Strategic planning and foundation building",
    startPercentage: 0,
    endPercentage: 20,
    strategies: ["Political Consulting", "Digital Marketing", "Press Relations"]
  },
  {
    name: "Growth Phase",
    description: "Expanding reach and building momentum",
    startPercentage: 20,
    endPercentage: 50,
    strategies: ["TV Ads", "Direct Mail", "Canvassing"]
  },
  {
    name: "Peak Phase",
    description: "Maximum outreach and engagement",
    startPercentage: 50,
    endPercentage: 80,
    strategies: ["Political Consulting", "Text Banking", "Fundraising"]
  },
  {
    name: "GOTV Phase",
    description: "Get out the vote and final push",
    startPercentage: 80,
    endPercentage: 100,
    strategies: ["Direct Mail", "Digital Marketing", "Text Banking"]
  }
];

const STRATEGY_ICONS: Record<string, any> = {
  "Signage": MapPin,
  "Political Consulting": UserCheck,
  "TV Ads": Tv,
  "Canvassing": Users,
  "Direct Mail": Mail,
  "Digital Marketing": Globe2,
  "Staffing": Users,
  "Fundraising": DollarSign,
  "Text Banking": MessageSquare,
  "Press Relations": Newspaper
};

export function TopStrategies({ phase, status, office = '', location = '' }: TopStrategiesProps) {
  const [expandedStrategy, setExpandedStrategy] = useState<string | null>(null);
  const [hoveredStrategy, setHoveredStrategy] = useState<string | null>(null);
  const [strategies, setStrategies] = useState<MediaStrategy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getMediaStrategies = async () => {
      if (!office || !location) {
        setIsLoading(false);
        return;
      }

      try {
        setError(null);
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${import.meta.env.VITE_GEMINI_API_KEY}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{
                parts: [{
                  text: `As a senior campaign strategist, provide detailed media and outreach strategy recommendations for:

                  Office: ${office}
                  Location: ${location}
                  Phase: ${phase}
                  Campaign Status: ${status}

                  Emphasize the critical role of professional political consulting throughout the campaign.
                  Political consulting should be presented as a high-priority, strategic investment that:
                  - Provides data-driven decision making
                  - Offers expert guidance on messaging and targeting
                  - Ensures efficient resource allocation
                  - Maximizes campaign effectiveness through proven methodologies

                  For each strategy, provide:
                  1. Title (one of: Political Consulting, TV Ads, Canvassing, Direct Mail, Digital Marketing, Staffing, Fundraising, Text Banking, Signage)
                  2. Recommended budget percentage (total across all strategies should equal 100%)
                  3. Optimal campaign phase for deployment
                  4. Specific tactics and channels
                  5. Target audience demographics
                  6. Expected ROI metrics
                  7. Risk factors and mitigation strategies

                  Format response as JSON array with exact keys:
                  {
                    "title": string,
                    "description": string,
                    "priority": "high" | "medium" | "low",
                    "budgetPercentage": number,
                    "timeline": string,
                    "metrics": string,
                    "channels": string[],
                    "targetAudience": string,
                    "effectiveness": number,
                    "deploymentPhase": string,
                    "insights": {
                      "keyMetrics": [{"label": string, "value": string}],
                      "timeline": [{"phase": string, "activities": string[]}],
                      "resources": [{"type": string, "details": string}],
                      "risks": [{"type": string, "mitigation": string}]
                    }
                  }`
                }]
              }]
            })
          }
        );
        // we need to handle response with error if any
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        console.log("response====>",data)
        
        if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
          throw new Error('Invalid API response structure');
        }

        const text = data.candidates[0].content.parts[0].text;
        
        // Try to find a JSON array in the response text
        const jsonMatch = text.match(/\[\s*\{[\s\S]*\}\s*\]/);
        if (!jsonMatch) {
          throw new Error('No JSON array found in response');
        }
        console.log("jsonMatch====>",jsonMatch)
        try {
          const parsedStrategies = JSON.parse(jsonMatch[0]);
        console.log("parsedStrategies is not parsing correct =>",parsedStrategies)
          if (!Array.isArray(parsedStrategies)) {
            throw new Error('Parsed content is not an array');
          }

          // Validate each strategy has required fields
          parsedStrategies.forEach((strategy, index) => {
            if (!strategy.title || !strategy.description || !strategy.priority) {
              throw new Error(`Invalid strategy at index ${index}: missing required fields`);
            }
          });

          setStrategies(parsedStrategies);
        } catch (e) {
          console.error('Failed to parse strategies:', e);
          throw new Error('Failed to parse valid strategies from response');
        }
      } catch (error) {
        console.error('Error fetching media strategies:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch strategies');
        
        // Use fallback strategies
        const fallbackStrategies = getFallbackStrategies(office, location, phase);
        setStrategies(fallbackStrategies);
      } finally {
        setIsLoading(false);
      }
    };

    getMediaStrategies();
  }, [office, location, phase, status]);

  const priorityColors = {
    high: 'text-red-500',
    medium: 'text-yellow-500',
    low: 'text-green-500'
  };

  const toggleStrategy = (title: string) => {
    setExpandedStrategy(expandedStrategy === title ? null : title);
  };

  return (
    <div className="bg-[#1a2236] rounded-xl p-4 sm:p-6 space-y-6 sm:space-y-8">
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <h2 className="text-xl text-gray-400">Media & Outreach Strategy for {office}</h2>
          <div className="px-3 py-1.5 bg-[#026fb9]/20 rounded-full border border-[#026fb9]/20">
            <span className="text-sm text-[#026fb9]">{location} Focus</span>
          </div>
        </div>
        <p className="text-sm text-gray-500">
          AI-optimized media tactics for {phase} phase in {location}
        </p>
      </div>

      {/* Campaign Phases Timeline */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-400">Campaign Timeline</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {CAMPAIGN_PHASES.map((phase, index) => (
            <div key={phase.name} className="p-4 bg-white/5 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{phase.name}</h4>
                <span className="text-sm text-[#026fb9]">{phase.startPercentage}%-{phase.endPercentage}%</span>
              </div>
              <p className="text-sm text-gray-400">{phase.description}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {phase.strategies.map((strategy) => (
                  <span key={strategy} className="text-xs px-2 py-1 bg-white/10 rounded-full">
                    {strategy}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Strategy Cards */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white/5 rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <div className="p-4 bg-red-500/10 text-red-500 rounded-lg">
            {error}
          </div>
        ) : (
          strategies.map((strategy, index) => {
            const Icon = STRATEGY_ICONS[strategy.title] || Target;
            const isExpanded = expandedStrategy === strategy.title;
            const isHovered = hoveredStrategy === strategy.title;
            
            return (
              <motion.div
                key={strategy.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`overflow-hidden rounded-lg transition-all duration-300 ${
                  isExpanded || isHovered ? 'bg-white/10' : 'bg-white/5'
                }`}
                onMouseEnter={() => setHoveredStrategy(strategy.title)}
                onMouseLeave={() => setHoveredStrategy(null)}
              >
                <button
                  onClick={() => toggleStrategy(strategy.title)}
                  className="w-full p-4 text-left"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        strategy.priority === 'high' ? 'bg-red-500/10' :
                        strategy.priority === 'medium' ? 'bg-yellow-500/10' :
                        'bg-green-500/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${priorityColors[strategy.priority]}`} />
                      </div>
                      <div>
                        <div className="font-semibold">{strategy.title}</div>
                        <div className="text-sm text-gray-400">{strategy.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm px-2 py-1 rounded bg-white/5">
                          {strategy.budget}
                        </div>
                        <div className={`px-3 py-1 rounded-full ${
                          strategy.priority === 'high' ? 'bg-red-500/10 text-red-500' :
                          strategy.priority === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                          'bg-green-500/10 text-green-500'
                        }`}>
                          {strategy.priority} Priority
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      </motion.div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-6 space-y-6"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {strategy.insights.keyMetrics.map((metric, i) => (
                            <div key={i} className="p-3 bg-white/5 rounded-lg">
                              <div className="text-sm text-gray-400">{metric.label}</div>
                              <div className="text-lg font-semibold">{metric.value}</div>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-gray-400">Implementation Timeline</h4>
                            {strategy.insights.timeline.map((phase, i) => (
                              <div key={i} className="p-3 bg-white/5 rounded-lg">
                                <div className="font-medium mb-2">{phase.phase}</div>
                                <div className="space-y-2">
                                  {phase.activities.map((activity, j) => (
                                    <div key={j} className="flex items-center gap-2 text-sm text-gray-400">
                                      <ArrowRight className="w-3 h-3 text-[#026fb9]" />
                                      <span>{activity}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="space-y-4">
                            <h4 className="text-sm font-medium text-gray-400">Resources & Risks</h4>
                            <div className="space-y-2">
                              {strategy.insights.resources.map((resource, i) => (
                                <div key={i} className="p-2 bg-white/5 rounded-lg">
                                  <div className="text-sm font-medium">{resource.type}</div>
                                  <div className="text-sm text-gray-400">{resource.details}</div>
                                </div>
                              ))}
                            </div>
                            <div className="space-y-2">
                              {strategy.insights.risks.map((risk, i) => (
                                <div key={i} className="p-2 bg-white/5 rounded-lg">
                                  <div className="text-sm font-medium text-red-400">{risk.type}</div>
                                  <div className="text-sm text-gray-400">{risk.mitigation}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400">Timeline</div>
                            <div className="font-medium">{strategy.timeline}</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400">Target Metrics</div>
                            <div className="font-medium">{strategy.metrics}</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400">Audience</div>
                            <div className="font-medium">{strategy.targetAudience}</div>
                          </div>
                          <div className="p-3 bg-white/5 rounded-lg">
                            <div className="text-sm text-gray-400">Effectiveness</div>
                            <div className="font-medium">{strategy.effectiveness}%</div>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {strategy.channels.map((channel, i) => (
                            <div key={i} className="px-3 py-1 bg-white/5 rounded-full text-sm">
                              {channel}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-8 space-y-4">
        <div className="p-4 bg-[#026fb9]/10 rounded-lg border border-[#026fb9]/20">
          <h4 className="text-[#026fb9] font-semibold mb-2">Why Professional Political Consulting Matters</h4>
          <p className="text-sm text-gray-300">
            Expert political consulting can significantly increase your campaign's effectiveness through data-driven strategy, 
            proven methodologies, and expert guidance. Professional consultants bring years of experience and proven success 
            in winning campaigns across various offices and jurisdictions.
          </p>
        </div>

        <div className="p-4 bg-white/5 rounded-lg text-sm text-gray-400">
          <p className="mb-2">
            <strong>AI Insights Disclaimer:</strong> While these recommendations are generated using AI analysis of historical campaign data and current trends, 
            nothing replaces the value of experienced political consulting.
          </p>
          <p className="text-[#026fb9] font-medium">
            For a comprehensive campaign strategy and personalized consulting services, contact Sutton & Smart:
          </p>
          <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-4">
            <a href="tel:+14806187653" className="inline-flex items-center gap-2 text-white hover:text-[#026fb9] transition-colors">
              <Phone className="w-4 h-4" />
              +1 480-618-7653
            </a>
            <a href="mailto:hello@suttonsmart.com" className="inline-flex items-center gap-2 text-white hover:text-[#026fb9] transition-colors">
              <Mail className="w-4 h-4" />
              hello@suttonsmart.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function getFallbackStrategies(office: string, location: string, phase: CampaignPhase): MediaStrategy[] {
  const isLocalOffice = office.toLowerCase().includes('mayor') || 
                       office.toLowerCase().includes('council') || 
                       office.toLowerCase().includes('board');

  const baseStrategies: MediaStrategy[] = [
    {
      title: "Digital Advertising",
      description: `Targeted social media and search ads for ${location} voters`,
      priority: "high",
      timeline: "Continuous",
      metrics: "5% CTR",
      budget: "$2,500/month",
      channels: ["Facebook", "Instagram", "Google Ads"],
      targetAudience: "Voters 25-65",
      effectiveness: 85,
      insights: {
        keyMetrics: [
          { label: "Click Rate", value: "4.8%" },
          { label: "Conversion", value: "2.3%" },
          { label: "Reach", value: "50k/week" }
        ],
        timeline: [
          {
            phase: "Setup",
            activities: ["Create ad accounts", "Design creatives", "Set targeting"]
          },
          {
            phase: "Optimization",
            activities: ["A/B testing", "Audience refinement", "Budget allocation"]
          }
        ],
        resources: [
          { type: "Team", details: "Digital marketing manager" },
          { type: "Assets", details: "Ad creatives, landing pages" }
        ],
        risks: [
          { type: "Ad Fatigue", mitigation: "Rotate creatives weekly" },
          { type: "Cost Inflation", mitigation: "Set bid caps" }
        ]
      }
    }
  ];

  if (isLocalOffice) {
    baseStrategies.push({
      title: "Community Outreach",
      description: `Local events and door-to-door in ${location}`,
      priority: "high",
      timeline: "Weekly",
      metrics: "500 contacts/week",
      budget: "$1,000/month",
      channels: ["Events", "Door-to-door", "Local Groups"],
      targetAudience: "Local Residents",
      effectiveness: 90,
      insights: {
        keyMetrics: [
          { label: "Contact Rate", value: "35%" },
          { label: "Events", value: "4/month" },
          { label: "Volunteers", value: "25+" }
        ],
        timeline: [
          {
            phase: "Planning",
            activities: ["Map districts", "Train volunteers", "Schedule events"]
          },
          {
            phase: "Execution",
            activities: ["Weekly canvassing", "Event hosting", "Follow-up"]
          }
        ],
        resources: [
          { type: "Team", details: "Field coordinator + volunteers" },
          { type: "Materials", details: "Handouts, signs, badges" }
        ],
        risks: [
          { type: "Weather", mitigation: "Indoor backup plans" },
          { type: "Turnout", mitigation: "Multiple reminder system" }
        ]
      }
    });
  }

  return baseStrategies;
}